/* eslint-disable global-require */
import { I_CommentScene, I_Hotspot, I_Scene } from '@containers/Home/types';
import {
  debounce,
  generateRandomID,
  isServer,
  isValidHttpUrl,
  separateColorAndTransparency,
} from '@utils/helper';
import { renderToString } from 'react-dom/server';
import { Root, createRoot } from 'react-dom/client';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useState,
  memo,
  useImperativeHandle,
  useRef,
} from 'react';
import { useSelector } from 'react-redux';
import dynamic from 'next/dynamic';
import {
  selectConfigProjectionModeProject,
  selectConfigControlModeProject,
  selectConfigScreenshotCapture,
  selectHotspotConfigGlobal,
  selectPolygonConfigGlobal,
  selectListPostUsedOnTour,
  selectLanguageProject,
  selectIsFirstScene,
  selectDollhouseConfigGlobal,
  selectTenant,
} from '@containers/Home/selectors';
import { useCollaborationMode } from '@containers/Home/global/TourStats';
import { useCommentScene } from '@containers/Home/hook/useCommentScene';
import CommentSceneIcon, {
  TypeRefCommentSceneIcon,
} from '@containers/Home/components/Collaboration/CommentScene/CommentSceneIcon';
import HotspotMedia, {
  TypeRefHotspotMedia,
} from '@components/Hotspot/HotspotMedia';
import KRPANO from './KrpanoTemplate';
import { IPropKrpano, Type_RefFuncKrpano } from './types';
import { useInternalFunction } from './useInternalFunction';
import { BG_COLOR_DOLLHOUSE_DEFAULT } from '@containers/Home/constants';
import { toogleFullScreen } from '@utils/fullscreen';

const Hotspot = dynamic(() => import('@components/Hotspot'));
const HotspotCompact = dynamic(
  () => import('@components/Hotspot/HotspotCompact'),
);
const _window = isServer ? undefined : (window as any);

const KrpanoViewer = forwardRef(
  (
    {
      scene,
      listScene = [],
      urlTour,
      divId = 'container',
      krpanoId = 'krpanoContainer',
      dollhouseXml = '',
      onClickHotspot,
      onChangeAutoChangeScene,
      isLoaded,
      setIsLoaded,
      nadirConfig,
      children,
      styleContainer = { height: '100%' },
      isTurnOffFollowMode = false,
      onChangeScene,
    }: IPropKrpano,
    ref: React.Ref<Type_RefFuncKrpano>,
  ) => {
    const isLoadedRef = useRef(false);
    const isAllowTriggerSoundRef = useRef(false);
    const containerRef = useRef<HTMLDivElement>(null!);
    // NOTE: store to control close/unmount popover when change scene/commentScene
    const listCommentSceneIconRef = useRef<
      Map<string, TypeRefCommentSceneIcon>
    >(new Map([]));
    const listCommentSceneIconRootRef = useRef<Map<string, Root>>(new Map([]));
    // NOTE: store to control volume (mute/unmute) on hotspot media video
    const listHotspotMediaVideoRef = useRef<Map<string, TypeRefHotspotMedia>>(
      new Map([]),
    );

    const tenant = useSelector(selectTenant);
    const isFirstScene = useSelector(selectIsFirstScene);
    const listPost = useSelector(selectListPostUsedOnTour);
    const languageProject = useSelector(selectLanguageProject);
    const hotspotConfigGlobal = useSelector(selectHotspotConfigGlobal);
    const dollhouseConfigGlobal = useSelector(selectDollhouseConfigGlobal);
    const polygonHotspotConfigGlobal = useSelector(selectPolygonConfigGlobal);
    const configControlModeProject = useSelector(
      selectConfigControlModeProject,
    );
    const configProjectionModeProject = useSelector(
      selectConfigProjectionModeProject,
    );
    const configScreenshotCapture = useSelector(selectConfigScreenshotCapture);

    const [krpanoView, setKrpanoView] = useState<any>();
    const [isAllowTriggerSound, setIsAllowTriggerSound] = useState(false);

    const { onCreateCommentScene, onUpdateCommentScene, onDeleteCommentScene } =
      useCommentScene();
    const [, setIsShowDialogCreateCommentScene] = useCollaborationMode(
      'isShowDialogCreateCommentScene',
    );
    const [, setCommentSceneTemporary] = useCollaborationMode(
      'commentSceneTemporary',
    );
    const [, setCommentSelected] = useCollaborationMode('commentSceneSelected');

    const {
      sceneChilds,
      onRenderXmlKrpano,
      onTriggerChangeView,
      onTriggerChangeZoom,
      onTriggerSoundHotspot,
      onTriggerLoadCompleted,
      onAddHotspotNadirKrpano,
      onAddHotspotKrpanoUtils,
      onAddHotspotVRKrpanoUtils,
      onAddCommentSceneIconUtils,
      onApplyConfigPluginKrpanoForScene,
      onBackNormalViewFromLittlePlanetView,
    } = useInternalFunction({
      scene,
      krpanoId,
    });

    // NOTE: use ref to store state because function used in event onclick hotspot (state won't update on event's function)
    useEffect(() => {
      isAllowTriggerSoundRef.current = isAllowTriggerSound;
    }, [isAllowTriggerSound]);

    // NOTE: reset state when scene change => only trigger sound when isAllowTriggerSound = true

    /**
     * SECTION: LOAD_SCENE_TO_KRPANO
     * @param {scene}: current scene selected
     * @param {sceneXml}: default by null, use krpano_xml on media if exist.
     */

    const loadSceneToView = useCallback(
      (myscene: I_Scene) => {
        if (myscene && myscene.media && krpanoView) {
          const { transition, view, fovDefault } = onRenderXmlKrpano(
            configProjectionModeProject?.mode === 'little_planet',
          );

          const bgColorDollhouse = separateColorAndTransparency(
            dollhouseConfigGlobal?.bgColor || BG_COLOR_DOLLHOUSE_DEFAULT,
          );

          _window[krpanoId].set(
            'dollhouse_bg_color',
            bgColorDollhouse.color.replace('#', '0x'),
          );

          if (isFirstScene && !!dollhouseConfigGlobal?.isShowOnStart)
            _window[krpanoId].call('hideHTML()');

          krpanoView.loadScene(
            urlTour,
            null,
            myscene.media.krpano_xml || null,
            transition,
            view,
            myscene.mode || '',
            sceneChilds,
            myscene.configStaging || {},
            fovDefault,
            myscene.media.subType,
            dollhouseXml,
            isFirstScene && !!dollhouseConfigGlobal?.isShowOnStart,
          );
        }
      },
      [
        urlTour,
        krpanoId,
        krpanoView,
        sceneChilds,
        dollhouseXml,
        isFirstScene,
        onRenderXmlKrpano,
        dollhouseConfigGlobal?.bgColor,
        configProjectionModeProject?.mode,
        dollhouseConfigGlobal?.isShowOnStart,
      ],
    );

    /**
     * !SECTION: END_LOAD_SCENE_TO_KRPANO
     */

    const onAddHotspotVRKrpano = useCallback(
      async (hotspot: I_Hotspot) => {
        const { krpano, name, nameTitle } = onAddHotspotVRKrpanoUtils(hotspot);
        const onClick = () => {
          if (hotspot.type === 'point') onClickHotspot({ hotspot });
          else if (hotspot.type === 'sound')
            onTriggerSoundHotspot(hotspot, isAllowTriggerSoundRef.current);
          else if (hotspot.type === 'image')
            krpano.call(
              `open_galleries("${name}", "${(
                hotspot.config?.image?.galleries || []
              )
                .map(item => item.src)
                .join(',')}")`,
            );
          else if (hotspot.type === 'video')
            krpano.call(
              `open_video("${name}", "${hotspot?.config?.video?.url}")`,
            );
        };
        krpano.set(`hotspot['${name}'].onclick`, onClick);
        krpano.set(`hotspot['${nameTitle}'].onclick`, onClick);

        krpano.call(`addhotspot('${nameTitle}')`);
        krpano.call(`addhotspot('${name}')`);

        return new Promise(resolve => resolve(true));
      },
      [onAddHotspotVRKrpanoUtils, onClickHotspot, onTriggerSoundHotspot],
    );

    const onAddHotspotKrpano = useCallback(
      async (
        hotspot: I_Hotspot,
        isReloadHotspot = false,
        patchConfig: any = undefined,
      ) => {
        const {
          name,
          krpano,
          onConfigHotspotMedia,
          onConfigHotspotPolygon,
          onConfigHotspotInstructor,
        } = onAddHotspotKrpanoUtils(
          hotspot,
          scene?.media.subType === 'flat'
            ? true
            : !!hotspotConfigGlobal?.keepHotspotPositionWhenZooming,
          patchConfig,
        );

        if (scene?.media.subType === 'flat')
          krpano.set(`hotspot['${name}'].scale`, 0.01);

        async function renderHotspot() {
          krpano.set(`hotspot['${name}'].renderer`, 'css3d');
          const hs = krpano.get(`hotspot[${name}]`);
          const contentString = (
            <div
              id={name}
              className={`inline-flex ${
                hotspotConfigGlobal?.lockHotspot &&
                (hotspot.type !== 'media' ||
                  hotspot.config?.media?.type !== 'media-image')
                  ? 'absolute-center'
                  : ''
              }`}
            />
          );
          // krpano.set(`hotspot['${name}'].renderer`, 'css3d');
          // krpano.set(`hotspot['${name}'].type`, 'html');
          // krpano.set(`hotspot['${name}'].htmloverflow`, 'visible');
          // hs.html = renderToString(contentString).toString();
          hs['sprite'].innerHTML = renderToString(contentString).toString();
          const node = document.getElementById(name);
          if (node) {
            const root = createRoot(node);
            root.render(
              hotspot.type === 'media' ? (
                <HotspotMedia
                  ref={r => {
                    if (
                      r &&
                      hotspot.config?.media?.type === 'media-video' &&
                      !listHotspotMediaVideoRef.current.get(hotspot.id)
                    )
                      listHotspotMediaVideoRef.current.set(hotspot.id, r);
                  }}
                  onClickHotspot={onClickHotspot}
                  isSoundOn={isAllowTriggerSoundRef.current}
                  hotspot={hotspot}
                  listPost={listPost}
                />
              ) : hotspot.type === 'compact' ? (
                <HotspotCompact
                  hotspot={hotspot}
                  listScene={listScene}
                  languageSelected={languageProject}
                  onClick={({ type, config }) => {
                    const newHotspot = {
                      ...hotspot,
                      config: { ...hotspot.config },
                    };
                    if (type) newHotspot.type = type;
                    if (config) newHotspot.config[type] = config;
                    onClickHotspot({ hotspot: newHotspot });
                  }}
                />
              ) : (
                <Hotspot
                  onClick={() => {
                    if (hotspot.config?.sound !== undefined)
                      onTriggerSoundHotspot(
                        hotspot,
                        isAllowTriggerSoundRef.current,
                      );
                    else if (
                      hotspot.type === 'point' &&
                      hotspot.config?.point?.isTransition &&
                      hotspot.config?.point?.typeTransition === '3d'
                    ) {
                      krpano.call(`transition_click(${name})`);
                      setTimeout(() => {
                        // if (hotspot.config?.sound !== undefined)
                        //   onTriggerSoundHotspot(
                        //     hotspot,
                        //     isAllowTriggerSoundRef.current,
                        //   );
                        // else
                        onClickHotspot({ hotspot });
                      }, 100);
                    } else onClickHotspot({ hotspot });
                  }}
                  isHidden={
                    hotspot.type === 'sound' && !hotspot.config?.sound?.action
                  }
                  isHideTitleGlobal={!!hotspotConfigGlobal?.hideTitle}
                  isHidePreviewGlobal={!!hotspotConfigGlobal?.hidePreview}
                  isHideTitleOnTopGlobal={
                    !!hotspotConfigGlobal?.hideTitleOnTopHotspot
                  }
                  bgColorDefault={hotspotConfigGlobal?.bgColor}
                  iconColorDefault={hotspotConfigGlobal?.iconColor}
                  hotspot={hotspot}
                  listScene={listScene}
                  listPost={listPost}
                  onChangePopover={visible => onChangeAutoChangeScene(!visible)}
                />
              ),
            );
          }
        }

        if (hotspot.polygon && hotspot.polygon_config) {
          onConfigHotspotPolygon(polygonHotspotConfigGlobal);
          krpano.set(`hotspot['${name}'].onclick`, () => {
            if (krpano.get('webvr.isenabled')) {
              if (hotspot.type === 'point') onClickHotspot({ hotspot });
              else if (hotspot.type === 'image')
                krpano.call(
                  `open_galleries("${name}", "${(
                    hotspot.config?.image?.galleries || []
                  ).map(item => item.src)}")`,
                );
              else if (hotspot.type === 'video')
                krpano.call(
                  `open_video("${name}", "${
                    hotspot.config?.video?.url || ''
                  }")`,
                );
            } else {
              onClickHotspot({ hotspot });
            }
          });
        } else if (!hotspot.polygon) {
          krpano.set(`hotspot['${name}'].ath`, +hotspot.ath);
          krpano.set(`hotspot['${name}'].atv`, +hotspot.atv);
          // krpano.set(`hotspot['${name}'].renderer`, 'css3d');
          if (hotspot.type === 'chevron') {
            krpano.set(`hotspot['${name}'].style`, 'skin_hotspotstyle');
            krpano.set(`hotspot['${name}'].onclick`, () => {
              if (
                hotspot.config?.chevron?.isTransition &&
                hotspot.config?.chevron?.typeTransition === '3d'
              ) {
                krpano.call(`transition_click(${name})`);
                setTimeout(() => {
                  onClickHotspot({ hotspot });
                }, 100);
              } else onClickHotspot({ hotspot });
            });
          }
          if (hotspot.type === 'media') onConfigHotspotMedia();
          if (
            hotspot.type === 'instructor' &&
            hotspot.config?.instructor?.media?.src
          ) {
            onConfigHotspotInstructor(isAllowTriggerSoundRef.current);
            krpano.set(`hotspot['${name}'].onclick`, () => {
              onTriggerSoundHotspot(hotspot, isAllowTriggerSoundRef.current);
            });
          } else if (hotspot.type === 'patch') {
            krpano.set(
              `hotspot['${name}'].width`,
              hotspot.config?.patch?.width || 50,
            );
            krpano.set(
              `hotspot['${name}'].height`,
              hotspot.config?.patch?.height || 50,
            );
            krpano.set(`hotspot['${name}'].distorted`, true);
            krpano.set(`hotspot['${name}'].rx`, 0);
            krpano.set(`hotspot['${name}'].ry`, 0);
            krpano.set(`hotspot['${name}'].rz`, 0);
            krpano.set(`hotspot['${name}'].usecontentsize`, true);
            krpano.set(`hotspot[${name}].zoom`, true);

            const layers = hotspot.config?.patch?.layers || [];
            const topmostVisibleLayer = layers.find(
              layer => layer.visible !== false && layer.action === 'none',
            );
            const topmostHoverLayer = layers.find(
              layer => layer.visible !== false && layer.action === 'hover',
            );
            const topmostClickLayer = layers.find(
              layer => layer.visible !== false && layer.action === 'click',
            );

            const imageUrl =
              topmostVisibleLayer?.src || hotspot.config?.patch?.src || '';
            const cacheBustedUrl = imageUrl
              ? `${imageUrl}`
              : `data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAAXNSR0IArs4c6QAAAAtJREFUCB1jYAACAAAFAAGNu5vzAAAAAElFTkSuQmCC`;

            krpano.set(`hotspot['${name}'].url`, cacheBustedUrl);
            krpano.set(`hotspot['${name}'].zorder`, 3);
            if (patchConfig?.action) krpano.set(`hotspot['${name}'].zorder`, 1);

            if (topmostHoverLayer && hotspot.config?.patch) {
              await onAddHotspotKrpano(
                {
                  ...hotspot,
                  config: {
                    ...hotspot.config,
                    patch: {
                      ...hotspot.config?.patch,
                      layers: [],
                      src: topmostHoverLayer.src,
                    },
                  },
                },
                isReloadHotspot,
                { action: 'hover' },
              );
              krpano.set(`hotspot['${name}'].onover`, () => {
                krpano.call(`tween_hotspot(${name}, ${name}-hover);`);
              });
              krpano.set(`hotspot['${name}'].onout`, () => {
                krpano.call(`tween_hotspot(${name}-hover, ${name});`);
              });
            }
            if (topmostClickLayer && hotspot.config?.patch)
              krpano.set(`hotspot['${name}'].onclick`, () => {
                const url = krpano.get(`hotspot['${name}'].url`);
                krpano.call(
                  `click_hotspot(${name}, ${
                    url === cacheBustedUrl
                      ? topmostClickLayer.src
                      : cacheBustedUrl
                  });`,
                );
              });
          } else {
            krpano.set(
              `hotspot['${name}'].url`,
              `data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAAXNSR0IArs4c6QAAAAtJREFUCB1jYAACAAAFAAGNu5vzAAAAAElFTkSuQmCC`,
            );
            krpano.set(`hotspot['${name}'].width`, 1);
            krpano.set(`hotspot['${name}'].height`, 1);
            krpano.set(`hotspot['${name}'].renderer`, 'css3d');
          }

          await krpano.set(`hotspot['${name}'].onloaded`, async () => {
            if (
              hotspot.type !== 'patch' &&
              hotspot.type !== 'chevron' &&
              hotspot.type !== 'instructor'
            )
              renderHotspot();
          });
        }
        krpano.set(`hotspot[${name}].capture`, false);

        if (!isReloadHotspot) krpano.call(`addhotspot('${name}')`);
        else if (
          !hotspot.polygon &&
          hotspot.type !== 'patch' &&
          hotspot.type !== 'chevron' &&
          hotspot.type !== 'instructor'
        )
          renderHotspot();

        krpano.set(`hotspot[${name}].capture`, false);

        return new Promise(resolve => resolve(true));
      },
      [
        scene,
        listPost,
        listScene,
        onClickHotspot,
        languageProject,
        hotspotConfigGlobal,
        onTriggerSoundHotspot,
        onChangeAutoChangeScene,
        onAddHotspotKrpanoUtils,
        polygonHotspotConfigGlobal,
      ],
    );

    /**
     * SECTION: COMMENT_SCENE (COLLABORATION MODE)
     */

    const renderHTMLCommentSceneIcon = useCallback(
      async (krpano, name, commentScene: I_CommentScene) => {
        const hs = krpano.get(`hotspot[${name}]`);
        if (commentScene.isOwnerTour)
          krpano.set(`hotspot['${name}'].onDragend`, () => {
            const newData = {
              ath: krpano.get(`hotspot['${name}'].ath`),
              atv: krpano.get(`hotspot['${name}'].atv`),
            };
            if (
              Math.abs(Math.abs(commentScene.ath) - Math.abs(newData.ath)) <
                0.0001 &&
              Math.abs(Math.abs(commentScene.atv) - Math.abs(newData.atv)) <
                0.0001
            ) {
              console.log('not trigger event');
            } else
              onUpdateCommentScene({
                ...commentScene,
                ...newData,
                message: undefined,
                isResolved: undefined,
                scene_id: commentScene.scene,
              });
          });
        krpano.set(`hotspot['${name}'].onclick`, () => {
          setCommentSelected(commentScene);
        });
        let node = document.getElementById(name);
        if (node) {
          // NOTE: close all popover active on screen
          // NOTE: unmount old component when replace (fixed bug: 2 popover show same time)
          if (
            listCommentSceneIconRef.current.size &&
            listCommentSceneIconRef.current.get(commentScene.id)
          )
            listCommentSceneIconRef.current?.get(commentScene?.id)?.onClose();

          if (listCommentSceneIconRootRef.current.size) {
            const root = listCommentSceneIconRootRef.current.get(
              `${commentScene.id}_root`,
            );
            if (root) await root.unmount();
          }
        }
        const contentString = <div id={name} className="inline-flex" />;
        hs.sprite.innerHTML = renderToString(contentString).toString();
        node = document.getElementById(name);
        if (node) {
          const root = createRoot(node);
          listCommentSceneIconRootRef.current.set(
            `${commentScene.id}_root`,
            root,
          );
          root.render(
            <CommentSceneIcon
              ref={r => {
                if (r) listCommentSceneIconRef.current.set(commentScene.id, r);
              }}
              commentScene={commentScene}
              onCreateCommentScene={onCreateCommentScene}
              onUpdateCommentScene={onUpdateCommentScene}
              onDeleteCommentScene={onDeleteCommentScene}
            />,
          );
        }
      },
      [
        setCommentSelected,
        onCreateCommentScene,
        onDeleteCommentScene,
        onUpdateCommentScene,
      ],
    );

    const onAddCommentSceneKrpano = useCallback(
      (commentScene: I_CommentScene, isReloadHTML = false) => {
        const { krpano, name } = onAddCommentSceneIconUtils(commentScene);
        if (commentScene.isOwnerTour)
          krpano.set(`hotspot['${name}'].ondown`, 'draghotspot()');
        krpano.set(`hotspot['${name}'].onloaded`, () => {
          renderHTMLCommentSceneIcon(krpano, name, commentScene);
        });
        if (!isReloadHTML) krpano.call(`addhotspot('${name}')`);
        else renderHTMLCommentSceneIcon(krpano, name, commentScene);
      },
      [renderHTMLCommentSceneIcon, onAddCommentSceneIconUtils],
    );

    const onRemoveCommentSceneKrpano = useCallback(
      id =>
        _window[krpanoId].call(
          `removehotspot('commentscene-${id}-${krpanoId}')`,
        ),
      [krpanoId],
    );

    const onReplaceCommentSceneKrpano = useCallback(
      (commentScene: I_CommentScene) =>
        onAddCommentSceneKrpano(commentScene, true),
      [onAddCommentSceneKrpano],
    );

    /**
     * !SECTION: END_COMMENT_SCENE (COLLABORATION MODE)
     */

    /**
     * SECTION: LISTEN EVENT
     */
    const onReloadAllHotspots = useCallback(
      (hotspots: I_Hotspot[] = [], isReload = false) => {
        hotspots?.map(item => onAddHotspotKrpano(item, isReload));
      },
      [onAddHotspotKrpano],
    );

    const onChangeView = debounce(() => onTriggerChangeView(), 150);

    const onChangeZoom = debounce(() => onTriggerChangeZoom(), 150);

    const onLoadNewSceneVR = useCallback(() => {
      const sceneId = _window[krpanoId].get('sceneId');
      if (sceneId) {
        if (scene?.id === sceneId && !isFirstScene)
          window[krpanoId].call('reset_view()');
        else onChangeScene(sceneId);
      }
    }, [isFirstScene, krpanoId, onChangeScene, scene?.id]);

    const onLoadComplete = useCallback(async () => {
      if (scene && !isLoadedRef.current) {
        if (!isFirstScene || !dollhouseConfigGlobal?.isShowOnStart) {
          onApplyConfigPluginKrpanoForScene();

          if (
            isFirstScene &&
            scene?.config?.isLittlePlanet &&
            scene.media.subType !== 'flat' &&
            configProjectionModeProject?.mode !== 'little_planet'
          )
            onBackNormalViewFromLittlePlanetView();
          if (
            !scene?.config?.isLittlePlanet &&
            configProjectionModeProject?.mode !== 'little_planet'
          )
            _window[krpanoId].call(
              `tween(view.tx|view.ty|view.tz|view.ox|view.oy|view.oz|view.fisheye, calc(''+image.ox+'|'+image.oy+'|'+image.oz+'|0|0|0|0'), 0.1, default)`,
            );

          onTriggerLoadCompleted(
            isTurnOffFollowMode || scene.media.subType === 'flat',
            configControlModeProject.mode || 'drag',
          );

          scene.media.subType !== 'flat' &&
            nadirConfig &&
            onAddHotspotNadirKrpano(nadirConfig);

          if (scene?.media.subType === 'flat') {
            _window[krpanoId].set('view.rx', 0);
            _window[krpanoId].set('view.ry', 0);
            _window[krpanoId].set('view.rz', 0);
          }

          if (scene?.hotspots)
            for (let i = 0; i < scene.hotspots.length; i++) {
              const hotspot = scene.hotspots[i];
              if (
                hotspot.polygon ||
                hotspot.type === 'chevron' ||
                hotspot.type === 'patch' ||
                hotspot.type === 'instructor' ||
                !_window[krpanoId].get('webvr.isenabled')
              )
                await onAddHotspotKrpano(hotspot);
              if (
                !hotspot.polygon &&
                (hotspot.type === 'point' ||
                  (hotspot.type === 'video' &&
                    hotspot.config?.video?.url?.endsWith('.mp4')) ||
                  (hotspot.type === 'sound' && hotspot.config?.sound?.action) ||
                  hotspot.type === 'image')
              )
                await onAddHotspotVRKrpano(hotspot);
            }
        }
        // NOTE: init garrows plugin
        // _window[krpanoId].call('hide_hotspots_no_vr()');
        _window[krpanoId].call('garrows_init()');
        isLoadedRef.current = true;
        setIsLoaded(true);
      }
    }, [
      scene,
      krpanoId,
      setIsLoaded,
      nadirConfig,
      isFirstScene,
      onAddHotspotKrpano,
      isTurnOffFollowMode,
      onAddHotspotVRKrpano,
      dollhouseConfigGlobal,
      onTriggerLoadCompleted,
      onAddHotspotNadirKrpano,
      configControlModeProject.mode,
      configProjectionModeProject?.mode,
      onApplyConfigPluginKrpanoForScene,
      onBackNormalViewFromLittlePlanetView,
    ]);

    const onTriggerCreateCommentSceneKrpano = useCallback(() => {
      const x = _window[krpanoId].get('mouse.clickx');
      const y = _window[krpanoId].get('mouse.clicky');
      const location = _window[krpanoId].screentosphere(x, y);
      const commentSceneTemporary = {
        id: generateRandomID(),
        ath: location.x,
        atv: location.y,
        message: '',
        scene: scene?.id || '',
        scene_id: scene?.id || '',
      };
      setCommentSceneTemporary(commentSceneTemporary);
      setIsShowDialogCreateCommentScene(true);
    }, [
      scene,
      krpanoId,
      setCommentSceneTemporary,
      setIsShowDialogCreateCommentScene,
    ]);

    /**
     * !SECTION: END_LISTEN_EVENT
     */

    /**
     * SECTION: CROSS_FUNCTION
     */
    useImperativeHandle(
      ref,
      () => ({
        getContainer() {
          return containerRef.current;
        },
        // ! VIEW KRPANO
        viewPlanet() {
          _window[krpanoId].call('cm_littleplanet_view');
        },
        viewNormal() {
          _window[krpanoId].call('cm_normal_view');
        },
        viewMirrorBall() {
          _window[krpanoId].call('mirror_ball_view');
        },
        viewFullscreen() {
          toogleFullScreen();
          // _window[krpanoId].call('switch(fullscreen)');
          // _window[krpanoId].call(
          //   'tween(layer[fullscreen_exitbutton].alpha, 1)',
          // );
        },
        viewWebvr() {
          _window[krpanoId].call('webvr.enterVR()');
        },
        toggleGyro() {
          _window[krpanoId].call('toggleGyroscope()');
        },
        startAutoRotate({ speed = 10, wait_time = 20 }) {
          _window[krpanoId].call(`startrotation(${speed}, ${wait_time})`);
        },
        stopAutoRotate() {
          _window[krpanoId].call('stoprotation');
        },
        toLeft() {
          _window[krpanoId].call('set(hlookat_moveforce,-0.75)');
        },
        toRight() {
          _window[krpanoId].call('set(hlookat_moveforce,+0.75)');
        },
        stopToHorizontal() {
          _window[krpanoId].call('set(hlookat_moveforce,0)');
        },
        toUp() {
          _window[krpanoId].call('set(vlookat_moveforce,-0.75)');
        },
        toDown() {
          _window[krpanoId].call('set(vlookat_moveforce,+0.75)');
        },
        stopToVertical() {
          _window[krpanoId].call('set(vlookat_moveforce,0)');
        },
        removeAllChevronHotspot(hotspots: I_Hotspot[]) {
          hotspots
            .filter(item => item.type === 'chevron')
            .forEach(item => {
              const name = `hotspot-${item.id}-${krpanoId}-type${item.type}-${
                item.polygon ? 'ispolygon' : 'unpolygon'
              }`;
              _window[krpanoId].call(`removenavigationelements(${name})`);
            });
        },
        triggerSound() {
          setIsAllowTriggerSound(true);
        },
        destroySound() {
          if (_window[krpanoId]) _window[krpanoId].call(`destroy_sound()`);
        },
        muteAllSound(detailScene: I_Scene) {
          detailScene?.hotspots?.forEach(hotspot => {
            const name = `hotspot-${hotspot.id}-${krpanoId}-type${
              hotspot.type
            }-${hotspot.polygon ? 'ispolygon' : 'unpolygon'}`;
            if (hotspot.type === 'sound' && hotspot.config?.sound)
              _window[krpanoId].call(`toggle_mute_sound(${`${name}`}, true)`);
            else if (
              hotspot.type === 'instructor' &&
              hotspot.config?.instructor?.media
            )
              _window[krpanoId].set(`hotspot[${name}].muted`, true);
            else if (
              hotspot.type === 'media' &&
              hotspot.config?.media?.type === 'media-video' &&
              hotspot?.config?.media?.media?.src
            )
              listHotspotMediaVideoRef.current
                ?.get(hotspot.id)
                ?.onToggleMute(true);
          });
        },
        unmuteAllSound(detailScene: I_Scene) {
          detailScene?.hotspots?.forEach(hotspot => {
            const name = `hotspot-${hotspot.id}-${krpanoId}-type${
              hotspot.type
            }-${hotspot.polygon ? 'ispolygon' : 'unpolygon'}`;
            if (hotspot.type === 'sound' && hotspot.config?.sound)
              _window[krpanoId].call(`toggle_mute_sound(${`${name}`}, false)`);
            else if (
              hotspot.type === 'instructor' &&
              hotspot.config?.instructor?.media
            )
              _window[krpanoId].set(`hotspot[${name}].muted`, false);
            else if (
              hotspot.type === 'media' &&
              hotspot.config?.media?.type === 'media-video' &&
              hotspot?.config?.media?.media?.src
            )
              listHotspotMediaVideoRef.current
                ?.get(hotspot.id)
                ?.onToggleMute(false);
          });
        },
        get(key: string) {
          return _window[krpanoId].get(key);
        },
        set(key: string, value: string) {
          _window[krpanoId].set(key, value);
        },
        call(xml: string) {
          _window[krpanoId].call(xml);
        },
        onAddCommentScene(commentScene: I_CommentScene) {
          onAddCommentSceneKrpano(commentScene);
        },
        onRemoveCommentScene(id: string) {
          onRemoveCommentSceneKrpano(id);
        },
        onReplaceCommentScene(commentScene: I_CommentScene) {
          onReplaceCommentSceneKrpano(commentScene);
        },
        focusOnCommentScene(commentScene, func) {
          const call = `lookat(${commentScene.ath}, ${
            commentScene.atv
          }, null, null, true, true, ${func()})`;
          _window[krpanoId].call(call);
        },
        loadXml(xml: string) {
          _window[krpanoId].call(`load_menu_vr(${xml})`);
        },
      }),
      [
        krpanoId,
        onAddCommentSceneKrpano,
        onRemoveCommentSceneKrpano,
        onReplaceCommentSceneKrpano,
      ],
    );

    /**
     * SECTION: INIT_KRPANO
     */
    useEffect(() => {
      if (!krpanoView && krpanoId) setKrpanoView(KRPANO(krpanoId));
    }, [krpanoView, krpanoId]);

    useEffect(() => {
      if (krpanoView)
        krpanoView.init(
          divId,
          `${process.env.NEXT_PUBLIC_ASSETS_URL}/core/tour.xml`,
          {
            mypath: process.env.NEXT_PUBLIC_ASSETS_URL,
            watermarkEnabled: !configScreenshotCapture?.isOffWatermark,
            watermarkURL:
              configScreenshotCapture?.watermarkURL ||
              isValidHttpUrl(tenant?.logo?.src || '')
                ? tenant?.logo?.src
                : '',
          },
        );
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [krpanoView, divId]);

    useEffect(() => {
      if (krpanoView && urlTour) {
        setIsLoaded(false);
        isLoadedRef.current = false;
        if (scene) loadSceneToView(scene);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scene?._id, krpanoView]);

    useEffect(() => {
      if (_window[krpanoId] && krpanoView) {
        _window[krpanoId].set(
          'events.webvr_onexitvr',
          onReloadAllHotspots.bind(
            this,
            scene?.hotspots.filter(item => !item.polygon) || [],
            false,
          ),
        );
        _window[krpanoId].set(
          'events.reset_view_action',
          onReloadAllHotspots.bind(this, scene?.hotspots, false),
        );
        _window[krpanoId].addEventListener('onloadcomplete', onLoadComplete);
        _window[krpanoId].addEventListener('onviewchange', onChangeView);
        _window[krpanoId].addEventListener('onmousewheel', onChangeZoom);
        _window[krpanoId].addEventListener(
          'ondoubleclick',
          onTriggerCreateCommentSceneKrpano,
        );
      }
      return () => {
        if (_window[krpanoId]) {
          _window[krpanoId].removeEventListener(
            'onloadcomplete',
            onLoadComplete,
          );
          _window[krpanoId].removeEventListener('onviewchange', onChangeView);
          _window[krpanoId].removeEventListener('onmousewheel', onChangeZoom);
          _window[krpanoId].removeEventListener(
            'ondoubleclick',
            onTriggerCreateCommentSceneKrpano,
          );
          setIsAllowTriggerSound(false);
          listCommentSceneIconRef.current = new Map([]);
          listHotspotMediaVideoRef.current = new Map([]);
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [krpanoView, scene._id]);

    useEffect(() => {
      if (_window[krpanoId] && krpanoView)
        _window[krpanoId].addEventListener('onnewpanovr', onLoadNewSceneVR);
      return () => {
        if (_window[krpanoId])
          _window[krpanoId].removeEventListener(
            'onnewpanovr',
            onLoadNewSceneVR,
          );
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [krpanoView, scene, isFirstScene]);

    /**
     * NOTE: trigger play_sound_at_hotspot (only when hotspot loaded)
     */
    useEffect(() => {
      if (isLoaded && isAllowTriggerSound)
        scene?.hotspots?.forEach(hotspot => {
          if (
            (hotspot.type === 'sound' &&
              hotspot.config?.sound !== undefined &&
              hotspot.config?.sound?.auto_play) ||
            (hotspot.type === 'instructor' &&
              hotspot.config?.instructor?.media &&
              !hotspot.config?.instructor?.paused_on_start)
          ) {
            onTriggerSoundHotspot(hotspot, isAllowTriggerSoundRef.current);
          } else if (
            hotspot.type === 'media' &&
            hotspot.config?.media?.type === 'media-video' &&
            hotspot?.config?.media?.volume &&
            listHotspotMediaVideoRef.current.get(hotspot.id)
          )
            listHotspotMediaVideoRef.current
              ?.get(hotspot.id)
              ?.onToggleMute(false);
        });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoaded, isAllowTriggerSound]);

    // NOTE: update hotspot's title multiLang
    useEffect(() => {
      if (languageProject && isLoaded && isLoadedRef.current)
        onReloadAllHotspots(scene?.hotspots || [], true);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [languageProject]);

    return (
      <div
        id={divId}
        ref={containerRef}
        style={{
          ...{
            width: '100%',
            height: '250px',
          },
          ...styleContainer,
        }}
      >
        {children}
      </div>
    );
  },
);

export default memo(KrpanoViewer);
